/**
 * /assistants/:agentId/chat — the assistant chat window (AgentStudioRoute).
 *
 * Session sidebar · markdown messages · tool-activity chips · EmailCard /
 * EmailListCard (rich rendering of Gmail tool results) · ActionPreviewCard
 * with Approve / Reject (the confirm protocol for sends, trash, filters, and
 * custom-connector calls) · composer with attachments (agent-uploads) and
 * dictation (FutureBot's hook).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box, Heading, Text, HStack, VStack, Button, IconButton, Badge, Spinner, Textarea,
  Tag, TagLabel, TagCloseButton, useColorModeValue, useToast, Collapse, Divider, Link, Tooltip,
} from '@chakra-ui/react';
import {
  FiArrowLeft, FiSend, FiPlus, FiTrash2, FiPaperclip, FiMic, FiMicOff, FiSettings,
  FiMail, FiChevronDown, FiChevronUp, FiCheck, FiX, FiTool, FiExternalLink, FiPaperclip as FiAttach,
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { callAgentStudio, useAssistantAttachments } from '../../hooks/useAssistants';
import { useSpeechDictation } from '../../components/FutureBot/useSpeechDictation';
import { useAssistantChat } from './useAssistantChat';

/** Strip the server's UNTRUSTED wrapper for display (it's a model-safety marker, not UI). */
function stripUntrusted(text) {
  return String(text || '')
    .replace(/<<<UNTRUSTED EMAIL CONTENT[^>]*>>>\n?/g, '')
    .replace(/\n?<<<END UNTRUSTED EMAIL CONTENT>>>/g, '')
    .trim();
}

const gmailLink = (id) => `https://mail.google.com/mail/u/0/#all/${id}`;

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_) { return ''; }
};

// ------------------------------------------------------------------ cards

function EmailRow({ email, cardBorder, subtle }) {
  const [open, setOpen] = useState(false);
  return (
    <Box borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={2.5}>
      <HStack justify="space-between" align="start" cursor="pointer" onClick={() => setOpen((v) => !v)}>
        <Box minW={0}>
          <HStack spacing={2}>
            {email.unread && <Box w={2} h={2} borderRadius="full" bg="blue.400" flexShrink={0} />}
            <Text fontSize="sm" fontWeight={email.unread ? '700' : '500'} noOfLines={1}>
              {email.from?.name || email.from?.email || 'Unknown sender'}
            </Text>
            <Text fontSize="xs" color={subtle} flexShrink={0}>{fmtWhen(email.receivedAt)}</Text>
          </HStack>
          <Text fontSize="sm" noOfLines={1}>{email.subject}</Text>
          {!open && <Text fontSize="xs" color={subtle} noOfLines={1}>{stripUntrusted(email.snippet)}</Text>}
        </Box>
        <IconButton size="xs" variant="ghost" aria-label="Expand" icon={open ? <FiChevronUp /> : <FiChevronDown />} />
      </HStack>
      <Collapse in={open} animateOpacity>
        <Divider my={2} />
        {email.body ? (
          <Text fontSize="sm" whiteSpace="pre-wrap" maxH="300px" overflowY="auto">{stripUntrusted(email.body)}</Text>
        ) : (
          <Text fontSize="sm" color={subtle}>{stripUntrusted(email.snippet)}</Text>
        )}
        {(email.attachments || []).length > 0 && (
          <HStack mt={2} flexWrap="wrap">
            {email.attachments.map((a) => (
              <Tag key={a.attachmentId || a.filename} size="sm" variant="subtle"><FiAttach style={{ marginRight: 4 }} />{a.filename}</Tag>
            ))}
          </HStack>
        )}
        <Link href={gmailLink(email.id)} isExternal fontSize="xs" color="blue.400" mt={2} display="inline-flex" alignItems="center" gap={1}>
          Open in Gmail <FiExternalLink />
        </Link>
      </Collapse>
    </Box>
  );
}

function ToolResultCard({ result, cardBorder, subtle }) {
  const ui = result?.ui?.type;
  if (ui === 'email') {
    return <EmailRow email={result.email} cardBorder={cardBorder} subtle={subtle} />;
  }
  if (ui === 'email_list') {
    return (
      <VStack align="stretch" spacing={2}>
        <HStack>
          <FiMail />
          <Text fontSize="xs" color={subtle}>
            {result.count} result(s){result.resultSizeEstimate > result.count ? ` of ~${result.resultSizeEstimate}` : ''} for <b>{result.query}</b>
          </Text>
        </HStack>
        {(result.emails || []).filter((e) => !e.error).map((e) => (
          <EmailRow key={e.id} email={e} cardBorder={cardBorder} subtle={subtle} />
        ))}
      </VStack>
    );
  }
  if (ui === 'thread') {
    return (
      <VStack align="stretch" spacing={2}>
        <Text fontSize="xs" color={subtle}>Conversation — {result.messageCount} message(s)</Text>
        {(result.messages || []).map((m) => (
          <EmailRow key={m.id} email={{ ...m, snippet: m.body }} cardBorder={cardBorder} subtle={subtle} />
        ))}
      </VStack>
    );
  }
  if (ui === 'draft_created') {
    return (
      <HStack borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={2.5} spacing={2}>
        <FiCheck color="green" />
        <Text fontSize="sm">Draft saved to your Gmail Drafts{result.to ? ` (to ${result.to})` : ''}: <b>{result.subject || '(no subject)'}</b></Text>
      </HStack>
    );
  }
  return null; // generic results stay behind the tool chip
}

function ActionPreviewCard({ pending, onApprove, onReject, busy, cardBg, cardBorder, subtle }) {
  const p = pending.preview || {};
  const email = p.email;
  return (
    <Box borderWidth="2px" borderColor="orange.300" bg={cardBg} borderRadius="lg" p={4} boxShadow="md">
      <HStack mb={2} justify="space-between">
        <HStack><FiTool /><Text fontWeight="700" fontSize="sm">{p.title || pending.toolName}</Text></HStack>
        <Badge colorScheme="orange">Needs your approval</Badge>
      </HStack>

      {email && (
        <Box borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={3} mb={2} fontSize="sm">
          <Text><b>From:</b> {email.from}</Text>
          <Text><b>To:</b> {email.to}</Text>
          {email.cc ? <Text><b>Cc:</b> {email.cc}</Text> : null}
          {email.bcc ? <Text><b>Bcc:</b> {email.bcc}</Text> : null}
          <Text><b>Subject:</b> {email.subject}</Text>
          <Divider my={2} />
          <Text whiteSpace="pre-wrap" maxH="260px" overflowY="auto">{email.body}</Text>
        </Box>
      )}

      {p.kind === 'trash_emails' && (
        <VStack align="stretch" spacing={1} mb={2}>
          {(p.emails || []).map((e) => (
            <Text key={e.id} fontSize="sm">• {e.from?.name || e.from?.email || ''} — {e.subject}</Text>
          ))}
        </VStack>
      )}

      {p.kind === 'create_filter' && (
        <Box fontSize="sm" mb={2}>
          <Text><b>When:</b> {JSON.stringify(p.criteria)}</Text>
          <Text><b>Then:</b> {(p.actions || []).join(', ')}</Text>
        </Box>
      )}

      {p.kind === 'delete_filter' && p.filter && (
        <Box fontSize="sm" mb={2}>
          <Text><b>Filter:</b> {JSON.stringify(p.filter.criteria || {})}</Text>
          <Text><b>Actions:</b> {JSON.stringify(p.filter.action || {})}</Text>
        </Box>
      )}

      {p.kind === 'mcp_tool' && (
        <Box fontSize="sm" mb={2}>
          <Text><b>Connector:</b> {p.connector}</Text>
          <Text><b>Tool:</b> {p.tool}</Text>
          <Box as="pre" fontSize="xs" mt={1} p={2} borderWidth="1px" borderColor={cardBorder} borderRadius="md" overflowX="auto">
            {JSON.stringify(p.args || {}, null, 2)}
          </Box>
        </Box>
      )}

      {p.note && <Text fontSize="xs" color={subtle} mb={2}>{p.note}</Text>}

      <HStack>
        <Button size="sm" colorScheme="green" leftIcon={<FiCheck />} onClick={() => onApprove(pending.id)} isLoading={busy}>
          Approve
        </Button>
        <Button size="sm" variant="outline" colorScheme="red" leftIcon={<FiX />} onClick={() => onReject(pending.id)} isDisabled={busy}>
          Reject
        </Button>
      </HStack>
    </Box>
  );
}

// --------------------------------------------------------------- message row

function MessageRow({ msg, userBubble, botBubble, cardBorder, subtle }) {
  const parts = msg.parts || {};

  if (msg.role === 'tool') {
    const card = <ToolResultCard result={parts.result} cardBorder={cardBorder} subtle={subtle} />;
    if (card && parts.result?.ui) {
      return <Box maxW="85%" alignSelf="flex-start" w="100%">{card}</Box>;
    }
    return null; // plain tool results are represented by the chips on the assistant turn
  }

  if (msg.role === 'assistant') {
    return (
      <Box alignSelf="flex-start" maxW="85%">
        {(parts.toolCalls || []).length > 0 && (
          <HStack mb={1} flexWrap="wrap" spacing={1}>
            {parts.toolCalls.map((c) => (
              <Tag key={c.id} size="sm" variant="subtle" colorScheme="purple">
                <FiTool style={{ marginRight: 4 }} />
                <TagLabel>{c.name}</TagLabel>
              </Tag>
            ))}
          </HStack>
        )}
        {msg.content && (
          <Box bg={botBubble} borderRadius="lg" px={4} py={2.5} fontSize="sm"
            sx={{
              '& p': { mb: 2 }, '& p:last-child': { mb: 0 },
              '& ul, & ol': { pl: 5, mb: 2 },
              '& code': { fontSize: '0.85em', px: 1, borderRadius: 'sm', bg: 'blackAlpha.200' },
              '& pre': { p: 2, borderRadius: 'md', overflowX: 'auto', bg: 'blackAlpha.300', mb: 2 },
              '& table': { width: '100%', mb: 2, fontSize: 'xs' },
              '& th, & td': { borderWidth: '1px', px: 2, py: 1 },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
          </Box>
        )}
      </Box>
    );
  }

  // user
  const isAction = parts.type === 'action_approved' || parts.type === 'action_rejected';
  if (isAction) {
    return (
      <HStack alignSelf="center" my={1}>
        <Tag size="sm" colorScheme={parts.type === 'action_approved' ? 'green' : 'red'} variant="subtle">
          {parts.type === 'action_approved' ? <FiCheck style={{ marginRight: 4 }} /> : <FiX style={{ marginRight: 4 }} />}
          {parts.type === 'action_approved' ? 'Approved' : 'Rejected'}
        </Tag>
      </HStack>
    );
  }
  return (
    <Box alignSelf="flex-end" maxW="75%">
      {(parts.attachments || []).length > 0 && (
        <HStack justify="flex-end" mb={1} flexWrap="wrap" spacing={1}>
          {parts.attachments.map((a, i) => (
            <Tag key={i} size="sm" variant="subtle"><FiAttach style={{ marginRight: 4 }} />{a.name}</Tag>
          ))}
        </HStack>
      )}
      {msg.content && (
        <Box bg={userBubble} color="white" borderRadius="lg" px={4} py={2.5} fontSize="sm" whiteSpace="pre-wrap">
          {msg.content}
        </Box>
      )}
    </Box>
  );
}

// --------------------------------------------------------------------- page

export default function AssistantChatPage() {
  const { agentId } = useParams();
  const history = useHistory();
  const toast = useToast();

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.600', 'gray.400');
  const userBubble = useColorModeValue('blue.500', 'blue.600');
  const botBubble = useColorModeValue('gray.100', 'gray.700');
  const sidebarBg = useColorModeValue('white', 'gray.800');
  const activeBg = useColorModeValue('blue.50', 'blue.900');

  const [agent, setAgent] = useState(null);
  const [draft, setDraft] = useState('');
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const chat = useAssistantChat(agentId);
  const attach = useAssistantAttachments(chat.sessionId || 'new');
  const dictation = useSpeechDictation({
    onFinal: (chunk) => setDraft((d) => (d ? `${d} ${chunk}` : chunk)),
  });

  useEffect(() => {
    callAgentStudio('getAssistant', { id: agentId })
      .then((d) => setAgent(d.agent))
      .catch((err) => {
        toast({ title: 'Could not open assistant', description: err.message, status: 'error', duration: 6000 });
        history.push('/assistants');
      });
  }, [agentId, history, toast]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat.messages, chat.pendingAction, chat.sending]);

  useEffect(() => {
    if (chat.error) toast({ title: 'Assistant error', description: chat.error, status: 'error', duration: 8000 });
  }, [chat.error, toast]);

  const doSend = () => {
    if (attach.isUploading) {
      toast({ title: 'Still uploading…', status: 'info', duration: 2000 });
      return;
    }
    const ready = attach.attachments.filter((a) => a.status === 'done');
    dictation.abort();
    chat.send(draft, ready);
    setDraft('');
    attach.clearAttachments();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      doSend();
    }
  };

  return (
    <Box minH="100vh" bg={pageBg} display="flex">
      {/* ------------------------------------------------------- sidebar */}
      <Box w="260px" bg={sidebarBg} borderRightWidth="1px" borderColor={cardBorder} p={3} display={{ base: 'none', md: 'flex' }} flexDirection="column" h="100vh" position="sticky" top="0">
        <HStack mb={3}>
          <IconButton icon={<FiArrowLeft />} size="sm" variant="ghost" aria-label="Back" onClick={() => history.push('/assistants')} />
          <Box minW={0}>
            <Heading size="xs" noOfLines={1}>{agent?.name || '…'}</Heading>
            <Text fontSize="2xs" color={subtle} noOfLines={1}>{agent?.type}</Text>
          </Box>
        </HStack>
        <Button size="sm" leftIcon={<FiPlus />} mb={3} onClick={chat.newSession}>New chat</Button>
        <VStack align="stretch" spacing={1} overflowY="auto" flex="1">
          {chat.sessions.map((s) => (
            <HStack
              key={s.id}
              px={2} py={1.5}
              borderRadius="md"
              bg={s.id === chat.sessionId ? activeBg : 'transparent'}
              _hover={{ bg: s.id === chat.sessionId ? activeBg : botBubble }}
              cursor="pointer"
              onClick={() => chat.openSession(s.id)}
              role="group"
            >
              <Box minW={0} flex="1">
                <Text fontSize="xs" fontWeight="600" noOfLines={1}>{s.title || 'Untitled'}</Text>
                <Text fontSize="2xs" color={subtle}>{fmtWhen(s.last_message_at || s.created_at)}</Text>
              </Box>
              <IconButton
                icon={<FiTrash2 />} size="xs" variant="ghost" aria-label="Delete session"
                opacity={0} _groupHover={{ opacity: 1 }}
                onClick={(e) => { e.stopPropagation(); chat.deleteSession(s.id); }}
              />
            </HStack>
          ))}
        </VStack>
        <Button size="xs" variant="ghost" leftIcon={<FiSettings />} mt={2} onClick={() => history.push(`/assistants/${agentId}/edit`)}>
          Customize assistant
        </Button>
      </Box>

      {/* ---------------------------------------------------------- main */}
      <Box flex="1" display="flex" flexDirection="column" h="100vh">
        <Box flex="1" overflowY="auto" px={{ base: 3, md: 8 }} py={6}>
          <VStack align="stretch" spacing={3} maxW="860px" mx="auto">
            {chat.loadingSession && <Box textAlign="center" py={8}><Spinner /></Box>}

            {!chat.loadingSession && chat.messages.length === 0 && (
              <Box textAlign="center" py={16}>
                <Heading size="md" mb={2}>{agent?.name || 'Assistant'}</Heading>
                <Text fontSize="sm" color={subtle} maxW="440px" mx="auto">{agent?.description}</Text>
                {(agent?.connector_keys || []).includes('gmail') && (
                  <HStack justify="center" mt={4} spacing={2} flexWrap="wrap">
                    {['What needs my attention in my inbox?', 'Find unread emails from this week', 'Draft a follow-up to my last client email'].map((s) => (
                      <Button key={s} size="xs" variant="outline" onClick={() => setDraft(s)}>{s}</Button>
                    ))}
                  </HStack>
                )}
              </Box>
            )}

            {chat.messages.map((m) => (
              <MessageRow key={m.id} msg={m} userBubble={userBubble} botBubble={botBubble} cardBorder={cardBorder} subtle={subtle} />
            ))}

            {chat.pendingAction && (
              <ActionPreviewCard
                pending={chat.pendingAction}
                onApprove={chat.approve}
                onReject={chat.reject}
                busy={chat.sending}
                cardBg={cardBg} cardBorder={cardBorder} subtle={subtle}
              />
            )}

            {chat.sending && (
              <HStack alignSelf="flex-start" px={2}>
                <Spinner size="sm" />
                <Text fontSize="xs" color={subtle}>Working…</Text>
              </HStack>
            )}
            <div ref={bottomRef} />
          </VStack>
        </Box>

        {/* ------------------------------------------------------ composer */}
        <Box borderTopWidth="1px" borderColor={cardBorder} bg={cardBg} px={{ base: 3, md: 8 }} py={3}>
          <Box maxW="860px" mx="auto">
            {attach.attachments.length > 0 && (
              <HStack mb={2} flexWrap="wrap" spacing={2}>
                {attach.attachments.map((a) => (
                  <Tag key={a.id} size="sm" variant="subtle" colorScheme={a.status === 'done' ? 'blue' : 'yellow'}>
                    <TagLabel>{a.name}{a.status === 'uploading' ? ` ${a.progress}%` : ''}</TagLabel>
                    <TagCloseButton onClick={() => attach.removeAttachment(a.id)} />
                  </Tag>
                ))}
              </HStack>
            )}
            {dictation.isListening && dictation.interimText && (
              <Text fontSize="xs" color={subtle} mb={1} fontStyle="italic">{dictation.interimText}…</Text>
            )}
            <HStack align="end" spacing={2}>
              <input ref={fileInputRef} type="file" hidden multiple accept="image/*,.pdf,.txt,.csv,.md,.json" onChange={(e) => { attach.addFiles(e.target.files); e.target.value = ''; }} />
              <Tooltip label="Attach files"><IconButton icon={<FiPaperclip />} variant="ghost" aria-label="Attach" onClick={() => fileInputRef.current?.click()} /></Tooltip>
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={chat.pendingAction ? 'Approve or reject the pending action above first…' : `Message ${agent?.name || 'assistant'}…`}
                rows={1}
                resize="none"
                minH="44px"
                maxH="160px"
                fontSize="sm"
                isDisabled={chat.sending}
              />
              <Tooltip label={dictation.isListening ? 'Stop dictation' : 'Dictate'}>
                <IconButton
                  icon={dictation.isListening ? <FiMicOff /> : <FiMic />}
                  variant={dictation.isListening ? 'solid' : 'ghost'}
                  colorScheme={dictation.isListening ? 'red' : undefined}
                  aria-label="Dictate"
                  onClick={() => (dictation.isListening ? dictation.stop() : dictation.start())}
                />
              </Tooltip>
              <IconButton
                icon={<FiSend />}
                colorScheme="blue"
                aria-label="Send"
                onClick={doSend}
                isDisabled={chat.sending || attach.isUploading || (!draft.trim() && attach.attachments.length === 0)}
              />
            </HStack>
            <Text fontSize="2xs" color={subtle} mt={1}>
              Sends, deletes, and filters always show you a preview card first. Email content is handled as untrusted data.
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
